import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
  NgZone,
  Output,
  EventEmitter,
  Inject,
} from '@angular/core';
import {
  ILayout,
  IScreenSize,
  ISection,
  ScreenMeasuringUnit,
} from '../../../models/user';
import { ISectionData, SectionType } from '../../../models/pageReader';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-page-reader-core',
  standalone: false,
  templateUrl: './page-reader-core.component.html',
  styleUrl: './page-reader-core.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class PageReaderCoreComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit, AfterViewChecked
{
  @Input() pageLayout: ILayout | null = null;
  @Input() showCorrectAnswers: boolean = false;
  @Output() quizInputChanged = new EventEmitter<boolean>();
  @ViewChild('gridContainer') gridContainer!: ElementRef;
  @ViewChild('grid') grid!: ElementRef;
  @ViewChildren('sectionElement') sectionElements!: QueryList<ElementRef>;

  private resizeObserver!: ResizeObserver;
  gridStyles: any = {};
  sections: any[] = [];
  defaultScreenSize: IScreenSize | null;
  private cellHeight = 0;
  private cellWidth = 0;
  private cellWidthOnLayout = 0;
  private cellHeightOnLayout = 0;
  private containerWidth = 0;
  private containerHeight = 0;
  private gridWidth = 0;
  private gridHeight = 0;
  private dpiX: number = 96;
  private dpiY: number = 96;
  private zoomScale: [number, number] = [1, 1];
  private container: HTMLElement | null = null;
  private initialQuizState: any = {}; // Store initial state of quiz inputs
  private hasQuizInputChanged: boolean = false; // Track if quiz inputs have changed
  private isPageLayoutChange: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private dialog: MatDialog,
  ) {
    this.defaultScreenSize = null;
    this.getScreenDPI().then(({ dpiX, dpiY }) => {
      this.dpiX = dpiX;
      this.dpiY = dpiY;
    });
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.initializeResizeObserver();
      this.drawGridLines();
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.drawSections();
      this.saveInitialQuizState();
      this.cdr.detectChanges();
    });
  }

  ngAfterViewChecked(): void {
    // this.applyBindingsToElements();
  }

  initializeResizeObserver() {
    if (!this.gridContainer) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.drawGridLines();
      this.drawSections();
      this.cdr.detectChanges();
      this.applyBindingsToElements();
    });

    this.resizeObserver.observe(this.gridContainer.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageLayout']?.currentValue) {
      setTimeout(() => {
        this.pageLayout = changes['pageLayout'].currentValue;
        this.isPageLayoutChange = true;
        if (this.pageLayout && this.pageLayout.Data) {
          const pageLayoutData = JSON.parse(this.pageLayout?.Data);
          this.defaultScreenSize = pageLayoutData.defaultScreenSize;
          this.cellHeightOnLayout = pageLayoutData.cellHeight;
          this.cellWidthOnLayout = pageLayoutData.cellWidth;
          if (this.defaultScreenSize) {
            this.getZoomScale(this.defaultScreenSize);
            this.grid.nativeElement.style.width = this.containerWidth + 'px';
            this.grid.nativeElement.style.height = this.containerHeight + 'px';
          }
        }
        this.drawGridLines();
        this.drawSections();
        this.saveInitialQuizState();
        this.cdr.detectChanges();
        this.applyBindingsToElements();
      }, 100);
    }
  }
  private getSizeInPixels(
    value: { width: number; height: number },
    fromUnit: ScreenMeasuringUnit,
  ) {
    if (fromUnit == ScreenMeasuringUnit.cm) {
      return {
        width: (value.width * this.dpiX) / 2.54,
        height: (value.height * this.dpiY) / 2.54,
      };
    } else if (fromUnit == ScreenMeasuringUnit.inch) {
      return {
        width: value.width * this.dpiX,
        height: value.height * this.dpiY,
      };
    } else {
      return value;
    }
  }
  private async getScreenDPI(): Promise<{ dpiX: number; dpiY: number }> {
    const divX = document.createElement('div');
    const divY = document.createElement('div');

    divX.style.width = '1in';
    divY.style.height = '1in';
    divX.style.position = divY.style.position = 'absolute';
    divX.style.visibility = divY.style.visibility = 'hidden';

    document.body.appendChild(divX);
    document.body.appendChild(divY);

    const dpiX = divX.offsetWidth;
    const dpiY = divY.offsetHeight;

    document.body.removeChild(divX);
    document.body.removeChild(divY);

    return { dpiX, dpiY };
  }
  private getZoomScale(layoutScreenSize: IScreenSize) {
    if (this.pageLayout) {
      //let sizeInPixel = this.getSizeInPixels({width:layoutScreenSize.width,height:layoutScreenSize.height},layoutScreenSize.unit);
      let sizeInPixel = {
        width: this.cellWidthOnLayout * this.pageLayout?.Cols,
        height: this.cellHeightOnLayout * this.pageLayout?.Rows,
      };

      this.cellWidthOnLayout = sizeInPixel.width / this.pageLayout.Cols;
      this.cellHeightOnLayout = sizeInPixel.height / this.pageLayout.Rows;
      let scale: [number, number] = [1, 1];
      if (sizeInPixel) {
        let layoutAR = sizeInPixel.width / sizeInPixel.height;
        let containerAR = this.gridWidth / this.gridHeight;
        if (layoutAR < containerAR) {
          let heightScale = (this.containerHeight * 1.0) / sizeInPixel.height;
          this.containerWidth = heightScale * sizeInPixel.width;
        } else if (layoutAR > containerAR) {
          let widthScale = (this.containerWidth * 1.0) / sizeInPixel.width;
          this.containerHeight = widthScale * sizeInPixel.height;
        }
        scale = [
          this.containerWidth / sizeInPixel.width,
          this.containerHeight / sizeInPixel.height,
        ];
        this.zoomScale = scale;
      }
    }
  }
  getLabelWidth(option: any): string {
    if (option?.labelMaxWidth) {
      const raw = option.labelMaxWidth || '100';
      const percent = raw.replace('%', '');
      const validPercent =
        isNaN(Number(percent)) || Number(percent) <= 0 ? '100' : percent;
      return `calc(${validPercent}% - 2px)`;
    } else {
      return `calc(100% - 2px)`;
    }
  }

  numberToWords(num: number): string {
    if (!Number.isFinite(num)) return '';
    if (num === 0) return 'zero';

    const belowTwenty: string[] = [
      '',
      'one',
      'two',
      'three',
      'four',
      'five',
      'six',
      'seven',
      'eight',
      'nine',
      'ten',
      'eleven',
      'twelve',
      'thirteen',
      'fourteen',
      'fifteen',
      'sixteen',
      'seventeen',
      'eighteen',
      'nineteen',
    ];

    const tens: string[] = [
      '',
      '',
      'twenty',
      'thirty',
      'forty',
      'fifty',
      'sixty',
      'seventy',
      'eighty',
      'ninety',
    ];

    const thousands: string[] = ['', 'thousand', 'million', 'billion'];

    const convert = (n: number): string => {
      if (n === 0) return '';
      if (n < 20) return belowTwenty[n];
      if (n < 100) {
        return (
          tens[Math.floor(n / 10)] +
          (n % 10 !== 0 ? ' ' + belowTwenty[n % 10] : '')
        );
      }
      return (
        belowTwenty[Math.floor(n / 100)] +
        ' hundred' +
        (n % 100 !== 0 ? ' ' + convert(n % 100) : '')
      );
    };

    let result: string = '';
    let index: number = 0;

    while (num > 0) {
      const chunk: number = num % 1000;
      if (chunk !== 0) {
        result =
          convert(chunk) +
          (thousands[index] ? ' ' + thousands[index] : '') +
          (result ? ' ' + result : '');
      }
      num = Math.floor(num / 1000);
      index++;
    }

    return result.trim();
  }

  // Quiz TextBox Feedback Message
  private getTextboxFeedbackMessage(option: any): {
    message1: string;
    message2: string;
    colorClass1: string;
    colorClass2: string;
    iconType: 'check' | 'cross';
  } | null {
    // Normalize user input
    const userValue: string = option.userValue?.trim() || '';
    const userValues: string[] = userValue
      ? userValue
          .split(',')
          .map((v: string) => v.trim())
          .filter(Boolean)
      : [];

    // Helper to normalize comma-separated values
    const normalize = (value: string): string[] =>
      value
        .split(',')
        .map((v: string) => v.trim())
        .filter(Boolean);

    const correctValues: string[] = normalize(option.correctValue || '');
    const optionalValues: string[] = normalize(option.optionalValue || '');
    const minimumOptionalValue: number = option.minimumOptionalValue
      ? Number(option.minimumOptionalValue)
      : 0;

    const hasCorrectValues = correctValues.length > 0;
    const hasOptionalValues = optionalValues.length > 0;
    const hasAnyAnswerDefined = hasCorrectValues || hasOptionalValues;

    // Pre-compute lowercase arrays for comparison
    const lowerCorrect = correctValues.map((v) => v.toLowerCase());
    const lowerOptional = optionalValues.map((v) => v.toLowerCase());
    const lowerUser = userValues.map((v) => v.toLowerCase());

    /* ---------------- CASE 1: User entered nothing ---------------- */
    if (!userValue) {
      // No answer expected
      if (!hasAnyAnswerDefined && minimumOptionalValue === 0) {
        return {
          message1: `Correct`,
          message2: 'This one stays blank.',
          colorClass1: 'green-text fs-2',
          colorClass2: 'black-text fs-1',
          iconType: 'check',
        };
      }

      // Correct values expected
      if (hasCorrectValues) {
        return {
          message1: 'Not Attempted.',
          message2: `Correct Answer:  ${correctValues.join(', ')}`,
          colorClass1: 'red-text fs-2',
          colorClass2: 'green-text fs-1',
          iconType: 'cross',
        };
      }

      // Optional values expected
      if (hasOptionalValues) {
        return {
          message1: 'Not Attempted.',
          message2: `Correct answer (any of): ${optionalValues.join(', ')}`,
          colorClass1: 'red-text fs-2',
          colorClass2: 'green-text fs-1',
          iconType: 'cross',
        };
      }
    }

    /* -------- User entered value but no answer is defined -------- */
    if (!hasAnyAnswerDefined) {
      return {
        message1: 'No answer expected.',
        message2: '',
        colorClass1: 'red-text fs-2',
        colorClass2: '',
        iconType: 'cross',
      };
    }

    /* -------- Correct values only (no optional values) -------- */
    if (hasCorrectValues && !hasOptionalValues) {
      const isValid = lowerUser.every((v) => lowerCorrect.includes(v));

      return isValid
        ? {
            message1: `Correct Answer: ${correctValues.join(', ')}`,
            message2: '',
            colorClass1: 'green-text fs-2',
            colorClass2: '',
            iconType: 'check',
          }
        : {
            message1: `Incorrect Answer: ${userValue}`,
            message2: `Correct Answer: ${correctValues.join(', ')}`,
            colorClass1: 'red-text fs-2',
            colorClass2: 'green-text fs-1',
            iconType: 'cross',
          };
    }

    /* -------- Optional values only (multiple choice) -------- */
    if (!hasCorrectValues && hasOptionalValues) {
      const allValid = lowerUser.every((v) => lowerOptional.includes(v));

      // User entered invalid values (not in optional list)
      // User entered more values than available options - incorrect
      if (!allValid || userValues.length > optionalValues.length) {
        return {
          message1: `Incorrect Answer: ${userValues.join(', ')}`,
          message2: `Correct Answer (any of): ${optionalValues.join(', ')}`,
          colorClass1: 'red-text fs-2',
          colorClass2: 'green-text fs-1',
          iconType: 'cross',
        };
      }

      // User entered all optional values
      if (userValues.length === optionalValues.length) {
        return userValues.length > minimumOptionalValue
          ? {
              message1: `Correct Answer: ${optionalValues.join(', ')}`,
              message2: '',
              colorClass1: 'green-text fs-2',
              colorClass2: '',
              iconType: 'check',
            }
          : {
              message1: `Incorrect Answer: ${userValues.join(', ')}`,
              message2: `Correct Answer (any ${this.numberToWords(
                minimumOptionalValue,
              )}): ${optionalValues.join(', ')}`,
              colorClass1: 'red-text fs-2',
              colorClass2: 'green-text fs-1',
              iconType: 'cross',
            };
      }

      // User entered some (but not all) valid optional values
      const otherOptions = optionalValues.filter(
        (ov) => !lowerUser.includes(ov.toLowerCase()),
      );

      return {
        message1: `Correct Answer: ${userValues.join(', ')}`,
        message2: `Other correct answer${
          otherOptions.length > 1 ? ' (any of)' : ''
        }: ${otherOptions.join(', ')}`,
        colorClass1: 'green-text fs-2',
        colorClass2: 'black-text fs-1',
        iconType: 'check',
      };
    }

    return null;
  }

  private getCheckboxFeedbackMessage(
    option: any,
  ): { message: string; colorClass: string } | null {
    if (option.isOptional && !option.isChecked) {
      return {
        message: 'Optional',
        colorClass: 'orange-text',
      };
    }

    return null;
  }

  // Handle the Quiz Checkbox Click
  onCheckboxClick(event: Event, sectionId: string, index: number) {
    // Only show dialog when showCorrectAnswers is true
    if (!this.showCorrectAnswers) {
      return;
    }

    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();

    const section = this.sections.find((s) => s.id === sectionId);
    if (section && section.options[index]) {
      const option = section.options[index];
      // If the Option is Not Checked, And Not Optional and Not Correct,
      // don't do anything, simply return
      if (!option.isCorrect && !option.isOptional && !option.isChecked) {
        return;
      }

      const feedbackData = this.getCheckboxFeedbackMessage(
        section.options[index],
      );
      if (!feedbackData) {
        return;
      }
      // Open the Dialog if the feedback data is not null
      feedbackData &&
        this.dialog.open(QuizAnswerFeedbackDialogComponent, {
          width: '400px',
          data: feedbackData,
          panelClass: 'quiz-feedback-dialog-component',
        });
    }
  }

  // When Quiz TextBox is Changed
  onTextboxClick(event: Event, sectionId: string, index: number) {
    // Only show dialog when showCorrectAnswers is true
    if (!this.showCorrectAnswers) {
      return;
    }

    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();

    const section = this.sections.find((s) => s.id === sectionId);
    if (section && section.options[index]) {
      const feedbackData = this.getTextboxFeedbackMessage(
        section.options[index],
      );
      if (!feedbackData) {
        return;
      }
      // Open the Dialog
      this.dialog.open(QuizTextboxFeedbackDialogComponent, {
        width: '400px',
        data: feedbackData,
        panelClass: 'quiz-feedback-dialog-component',
      });
    }
  }

  getTextboxInputClass(sectionId: string, optionIndex: number): string {
    if (!this.showCorrectAnswers) {
      return '';
    }

    const section = this.sections.find((s) => s.id === sectionId);
    if (!section?.options?.[optionIndex]) {
      return '';
    }

    const opt = section.options[optionIndex];

    // Parse user input
    const userHasNotEnteredAnything = !opt.userValue?.trim();
    const userValues = userHasNotEnteredAnything
      ? []
      : opt.userValue
          .split(',')
          .map((v: string) => v.trim().toLowerCase())
          .filter((v: string) => v !== '');

    // Parse correct and optional values
    const correctValues = (opt.correctValue || '')
      .split(',')
      .map((v: string) => v.trim().toLowerCase())
      .filter((v: string) => v !== '');

    const optionalValues = (opt.optionalValue || '')
      .split(',')
      .map((v: string) => v.trim().toLowerCase())
      .filter((v: string) => v !== '');

    // Add minimum optional value
    // const minimumOptionalValue: number = opt.minimumOptionalValue; // or use opt.minimumOptionalValue if available
    const minimumOptionalValue: number = opt.minimumOptionalValue
      ? Number(opt.minimumOptionalValue)
      : 0;

    const hasCorrectValueDefined = correctValues.length > 0;
    const hasOptionalValueDefined = optionalValues.length > 0;
    const hasAnyAnswerDefined =
      hasCorrectValueDefined || hasOptionalValueDefined;

    // CASE: User entered nothing
    if (userHasNotEnteredAnything) {
      // No answer expected and no minimum required
      if (!hasAnyAnswerDefined && minimumOptionalValue === 0) {
        return '';
      }
      // If any answer is expected, it's incorrect
      if (hasAnyAnswerDefined) {
        return 'textbox-input-incorrect';
      }
      // No answer defined and user didn't enter anything - neutral
      return '';
    }

    // CASE: User entered something but no answer is defined
    if (!hasAnyAnswerDefined) {
      return 'textbox-input-incorrect';
    }

    // Check if all user values are valid (within correct + optional)
    const allValidAnswers = [...correctValues, ...optionalValues];
    const allUserValuesAreValid = userValues.every((v: string) =>
      allValidAnswers.includes(v),
    );

    // If user entered invalid values
    if (!allUserValuesAreValid) {
      return 'textbox-input-incorrect';
    }

    // All user values are valid
    // Check if they have at least some correct values when correct is defined
    if (hasCorrectValueDefined) {
      const hasSomeCorrect = userValues.some((v: string) =>
        correctValues.includes(v),
      );
      return hasSomeCorrect
        ? 'textbox-input-correct'
        : 'textbox-input-incorrect';
    } else {
      // Only optional values defined
      // Check if user entered all optional values AND meets minimum requirement
      if (userValues.length === optionalValues.length) {
        return userValues.length > minimumOptionalValue
          ? 'textbox-input-correct'
          : 'textbox-input-incorrect';
      }
      // User entered some (but not all) optional values - still correct
      return 'textbox-input-correct';
    }
  }

  setBindingValue(
    target: any,
    bindingPath: string,
    value: any,
    sectionType: SectionType,
  ): void {
    if (!target || !bindingPath) {
      console.error('Invalid target or binding path', { target, bindingPath });
      return;
    }

    if (bindingPath.endsWith('style.transform') && typeof value === 'string') {
      // Check For Negative Values Also
      const match = value.match(/^(-?\d+(\.\d+)?)px$/);
      if (match) {
        value = `rotate(${match[1]}deg)`;
      }
    }

    const pathParts = bindingPath.split('.');
    if (pathParts.length === 1) {
      target[bindingPath] = value;
      return;
    }

    let current = target;
    let multipleTargets: any[] = [];
    let nodeList: any;

    // check for target
    if (pathParts.length > 1 && pathParts[0].startsWith('$')) {
      current = (target as HTMLElement).querySelector(
        pathParts[0].replace('$', '.'),
      );
      nodeList = (target as HTMLElement).querySelectorAll(
        pathParts[0].replace('$', '.'),
      );
      pathParts.splice(0, 1);
    }

    // Traverse the path to the second-to-last key
    for (let i = 0; i < pathParts.length - 1 && current; i++) {
      const key = pathParts[i];

      // If the key doesn't exist, log an error
      if (!(key in current)) {
        // console.error(`Invalid binding path: ${bindingPath}`)
        return;
      }

      current = current[key];
      if (i == 0 && nodeList) {
        for (var mi = 0; mi < nodeList.length; mi++) {
          multipleTargets.push(nodeList[mi][key]);
        }
      } else if (i > 0 && nodeList) {
        for (var mi = 0; mi < multipleTargets.length; mi++) {
          const target = multipleTargets[mi];
          if (target && typeof target === 'object') {
            multipleTargets[mi] = (target as any)[key];
          }
        }
      }
    }

    const finalKey = pathParts[pathParts.length - 1];
    if (current) {
      current[finalKey] = value;
      // These properties trigger GPU acceleration and more refined rendering pipelines (For Fixing the Zig-Zag Line Issue);
      // current["transform-style"] = "preserve-3d";
      // current["backface-visibility"] = "hidden";

      if (multipleTargets) {
        for (var mi = 0; mi < multipleTargets.length; mi++) {
          multipleTargets[mi][finalKey] = value;
        }
      }
      if (finalKey === 'padding') {
        const paddingValue = value.replace('px', '');
        current['height'] = `calc(100% - ${Number(paddingValue) * 2}px)`;
        current['width'] = `calc(100% - ${Number(paddingValue) * 2}px)`;
      }
      if (finalKey === 'gap') {
        const gapValue = Number(value.replace('px', ''));

        const chkBoxContainers = target.querySelectorAll(
          '.option-container',
        ) as HTMLInputElement[];
        const txtBoxContainers = target.querySelectorAll(
          '.textbox-input-container',
        ) as HTMLInputElement[];
        const txtBoxes = target.querySelectorAll(
          '.textbox-input-container input[type="text"]',
        ) as HTMLInputElement[];
        chkBoxContainers.forEach((container) => {
          const input = container.style.width;
          container.style.width = input.replace(/(\d+)px/, `${gapValue + 2}px`);
        });
        txtBoxContainers.forEach((container) => {
          const input = container.style.width;
          container.style.width = input.replace(/(\d+)px/, `${gapValue + 2}px`);
        });
        txtBoxes.forEach((txtBox) => {
          const input = txtBox.style.width;
          txtBox.style.width = input.replace(/(\d+)px/, `${gapValue + 2}px`);
        });
      } else if (finalKey === 'fontSize') {
        const chkBoxes = target.querySelectorAll(
          '.option-container input[type="checkbox"]',
        ) as HTMLInputElement[];
        const txtBoxes = target.querySelectorAll(
          '.textbox-input-container input[type="text"]',
        ) as HTMLInputElement[];
        const label = target.querySelector(
          '.option-container label',
        ) as HTMLInputElement;
        const checkboxOverlays = target.querySelectorAll(
          '.checkbox-overlay',
        ) as HTMLElement[];
        const checkboxCheckmarks = target.querySelectorAll(
          '.checkbox-checkmark',
        ) as SVGElement[];
        let lblHgtWithSL = 0;
        if (label) {
          const prevText = label.innerHTML;
          label.innerHTML = 'test';
          const computedStyle = window.getComputedStyle(label);
          lblHgtWithSL = parseFloat(computedStyle.height);
          label.innerHTML = prevText;
        }

        if (chkBoxes.length > 0) {
          const computedStyle = window.getComputedStyle(target);
          const fontSize = parseFloat(computedStyle.fontSize);
          chkBoxes.forEach((chkBox) => {
            let chkSize: number = 13;
            let scale = (fontSize * 1.0) / 12;
            let newChkSize = chkSize * scale;
            (chkBox as HTMLInputElement).style.marginTop = `${
              (lblHgtWithSL - newChkSize) / 2 + (newChkSize - chkSize) / 2
            }px`;
            (chkBox as HTMLInputElement).style.transform = `scale(${scale})`;
            (chkBox as HTMLInputElement).style.marginLeft = `${
              (newChkSize - chkSize) / 2
            }px`;
            (chkBox as HTMLInputElement).style.marginRight = `${
              (newChkSize - chkSize) / 2
            }px`;
          });
        }

        if (checkboxOverlays.length > 0) {
          const computedStyle = window.getComputedStyle(target);
          const fontSize = parseFloat(computedStyle.fontSize);
          let scale = (fontSize * 1.0) / 12; // same scale factor as checkbox
          let chkSize: number = 13;
          let newChkSize = chkSize * scale;
          checkboxOverlays.forEach((overlay) => {
            overlay.style.transform = `scale(${scale})`;
            overlay.style.marginTop = `${
              (lblHgtWithSL - newChkSize) / 2 + (newChkSize - chkSize) / 2
            }px`;
            overlay.style.marginLeft = `${(newChkSize - chkSize) / 2}px`;
            overlay.style.marginRight = `${(newChkSize - chkSize) / 2}px`;
          });
        }

        if (checkboxCheckmarks.length > 0) {
          const computedStyle = window.getComputedStyle(target);
          const fontSize = parseFloat(computedStyle.fontSize);
          let scale = (fontSize * 1.0) / 12;
          let chkSize: number = 13;
          let newChkSize = chkSize * scale;
          checkboxCheckmarks.forEach((checkmark) => {
            checkmark.style.transform = `scale(${scale})`;
            checkmark.style.marginTop = `${
              (lblHgtWithSL - newChkSize) / 2 + (newChkSize - chkSize) / 2
            }px`;
            checkmark.style.marginLeft = `${(newChkSize - chkSize) / 2}px`;
            checkmark.style.marginRight = `${(newChkSize - chkSize) / 2}px`;
          });
        }

        if (txtBoxes.length > 0) {
          const computedStyle = window.getComputedStyle(target);
          const fontSize = parseFloat(computedStyle.fontSize);
          txtBoxes.forEach((txtBox) => {
            (txtBox as HTMLInputElement).style.fontSize =
              computedStyle.fontSize;
          });
        }
      }
    }
    this.cdr.detectChanges();
  }

  drawGridLines() {
    if (!this.pageLayout || !this.gridContainer) return;

    const rows = this.pageLayout.Rows;
    const columns = this.pageLayout.Cols;

    const container = this.gridContainer.nativeElement;
    const rect = container.getBoundingClientRect();
    this.gridWidth = rect.width;
    this.gridHeight = rect.height;
    this.containerWidth = rect.width;
    this.containerHeight = rect.height;
    if (this.defaultScreenSize) {
      this.getZoomScale(this.defaultScreenSize);
      this.grid.nativeElement.style.width = this.containerWidth + 'px';
      this.grid.nativeElement.style.height = this.containerHeight + 'px';
    }
    this.cellWidth = this.containerWidth / columns;
    this.cellHeight = this.containerHeight / rows;

    // this.gridStyles = {
    //   'grid-template-columns': `repeat(${columns}, ${this.cellWidth}px)`,
    //   'grid-template-rows': `repeat(${rows}, ${this.cellHeight}px)`,
    //   '--cell-width': `${this.cellWidth}px`,
    //   '--cell-height': `${this.cellHeight}px`,
    // };
  }

  drawSections() {
    // Save the Previous Sections State, so that the checkbox and the textbox options are
    // reapplied when the page is resized
    const previousSections = this.isPageLayoutChange ? [] : [...this.sections];
    this.sections = [];

    this.pageLayout?.Sections?.forEach((section, index) => {
      this.drawSection(section, index);

      // Restore state from previous section at same index
      const previousSection = previousSections[index];
      const currentSection = this.sections[index];

      if (
        previousSection &&
        currentSection &&
        previousSection.type === currentSection.type
      ) {
        // Restore the Checkbox Option
        if (currentSection.type === SectionType.Option) {
          currentSection.options.forEach((opt: any, optIndex: number) => {
            if (previousSection.options?.[optIndex]) {
              opt.isChecked = previousSection.options[optIndex].isChecked;
            }
          });
        } // Restore the Textbox Option
        else if (currentSection.type === SectionType.TextBox) {
          currentSection.options.forEach((opt: any, optIndex: number) => {
            if (previousSection.options?.[optIndex]) {
              opt.userValue = previousSection.options[optIndex].userValue;
            }
          });
        }
      }
    });

    // Reset the Page Layout Change After drawing sections
    this.isPageLayoutChange = false;
  }

  private sectionDrawMethods: {
    [key in SectionType]?: (sectionData: ISectionData, index: number) => void;
  } = {
    [SectionType.Rectangle]: this.drawRectangle.bind(this),
    [SectionType.Image]: this.drawImage.bind(this),
    [SectionType.Text]: this.drawText.bind(this),
    [SectionType.RichText]: this.drawRichText.bind(this),
    [SectionType.Option]: this.drawOption.bind(this),
    [SectionType.Tags]: this.drawTags.bind(this),
    [SectionType.TextBox]: this.drawTextBox.bind(this),
    [SectionType.Line]: this.drawLine.bind(this),
    [SectionType.Arrow]: this.drawArrow.bind(this),
  };

  // Draw Rectangle
  drawRectangle(sectionData: ISectionData) {
    const rectangle = {
      type: SectionType.Rectangle,
    };
    this.sections.push(rectangle);
  }

  drawImage(sectionData: ISectionData) {
    const image = {
      type: SectionType.Image,
    };
    this.sections.push(image);
  }

  drawText(sectionData: ISectionData) {
    const text = {
      textContent: sectionData.textContent,
    };
    this.sections.push(text);
  }

  drawRichText(sectionData: ISectionData) {
    const richText = {
      textContent: sectionData.textContent,
      type: SectionType.RichText,
    };
    this.sections.push(richText);
  }

  drawOption(sectionData: ISectionData) {
    const options: any[] = sectionData.options || [];

    const optionSection = {
      type: SectionType.Option,
      options: options.map((opt) => ({
        label: opt.label,
        showLabel: opt.showLabel ?? true,
        isCorrect: opt.isCorrect ?? false,
        isOptional: opt.isOptional ?? false,
        labelMaxWidth: opt.labelMaxWidth,
        isChecked: false,
      })),
      id: sectionData.id || `option-section-${this.sections.length}`, // Fallback ID
      value: options.filter((o) => o.isCorrect).map((o) => o.label),
    };

    this.sections.push(optionSection);
  }

  drawTags(sectionData: ISectionData) {
    const tags = {};
    this.sections.push(tags);
  }

  drawTextBox(sectionData: ISectionData) {
    const textBoxOptions: any[] = sectionData.options || [];

    const textBox = {
      type: SectionType.TextBox,
      options: textBoxOptions.map((option) => ({
        optionalValue: option.optionalValue || '',
        minimumOptionalValue: option.minimumOptionalValue || 0,
        label: option.label || 'Label',
        correctValue: option.correctValue || '',
        userValue: '', // Track user input
        showLabel: option.showLabel ?? true,
        width: option.width || '100px',
        labelMaxWidth: option.labelMaxWidth,
        textAlign: option.textAlign || 'left',
      })),
      id: sectionData.id || `textbox-section-${this.sections.length}`, // Fallback ID
    };

    this.sections.push(textBox);
  }

  drawLine(sectionData: ISectionData) {
    const lineType =
      sectionData.propInfo?.find((p) => p.name === 'LineType')?.value ||
      'horizontal';
    const strokeColor =
      sectionData.propInfo?.find((p) => p.name === 'strokeColor')?.value ||
      '#000000';
    const strokeWidth =
      sectionData.propInfo?.find((p) => p.name === 'strokeWidth')?.value || '2';
    const fillType =
      sectionData.propInfo?.find((p) => p.name === 'fillType')?.value || 'none';
    const rotation =
      sectionData.propInfo?.find((p) => p.name === 'rotation')?.value || '0';

    const line = {
      type: SectionType.Line,
      lineType,
      strokeColor,
      strokeWidth,
      fillType,
      rotation,
      x1: this.getLineCoordinates(lineType).x1,
      y1: this.getLineCoordinates(lineType).y1,
      x2: this.getLineCoordinates(lineType).x2,
      y2: this.getLineCoordinates(lineType).y2,
    };

    this.sections.push(line);
  }

  private getLineCoordinates(lineType: string) {
    const lineCoordinates: any = {
      horizontal: { x1: '0', y1: '50%', x2: '100%', y2: '50%' },
      vertical: { x1: '50%', y1: '0', x2: '50%', y2: '100%' },
      diagonal: { x1: '0', y1: '0', x2: '100%', y2: '100%' },
    };
    return lineCoordinates[lineType] || lineCoordinates.horizontal;
  }

  private getArrowPath(
    direction: string,
    strokeWidth: number,
    height: number,
    width: number,
    index: number,
  ): string {
    // Validate inputs with robust checks
    const validWidth =
      Number.isFinite(Number(width)) && Number(width) > 0 ? Number(width) : 100;
    const validHeight =
      Number.isFinite(Number(height)) && Number(height) > 0
        ? Number(height)
        : 50;
    const validStrokeWidth =
      Number.isFinite(Number(strokeWidth)) && Number(strokeWidth) >= 0
        ? Number(strokeWidth)
        : 2;

    const arrowHeadHeight: number = 5 + validStrokeWidth;
    const arrowHeadLength: number = 10 + validStrokeWidth;
    const gap: number = validStrokeWidth / 2;
    const arrowPaths = {
      right: `M 0,${validHeight / 2} L ${validWidth - gap},${
        validHeight / 2
      } M ${validWidth - arrowHeadLength - gap},${
        validHeight / 2 - arrowHeadHeight
      } L ${validWidth - gap},${validHeight / 2} L ${
        validWidth - arrowHeadLength - gap
      },${validHeight / 2 + arrowHeadHeight}`,
      left: `M ${validWidth - gap},${validHeight / 2} L 0,${
        validHeight / 2
      } M ${arrowHeadLength},${validHeight / 2 - arrowHeadHeight} L 0,${
        validHeight / 2
      } L ${arrowHeadLength},${validHeight / 2 + arrowHeadHeight}`,
      up: `M ${validWidth / 2},${validHeight} L ${validWidth / 2},0 M ${
        validWidth / 2 - arrowHeadHeight
      },${arrowHeadLength} L ${validWidth / 2},0 L ${
        validWidth / 2 + arrowHeadHeight
      },${arrowHeadLength}`,
      down: `M ${validWidth / 2},0 L ${validWidth / 2},${validHeight} M ${
        validWidth / 2 - arrowHeadHeight
      },${validHeight - arrowHeadLength} L ${validWidth / 2},${validHeight} L ${
        validWidth / 2 + arrowHeadHeight
      },${validHeight - arrowHeadLength}`,
    };
    return arrowPaths[direction as keyof typeof arrowPaths] || arrowPaths.right;
  }

  onTextBoxChange(
    sectionId: string,
    options: any[],
    index: number,
    event: Event,
  ) {
    const input = event.target as HTMLInputElement;
    const userValue = input.value;

    // Update the section's userValue
    const section = this.sections.find((s) => s.id === sectionId);
    if (section && section.options[index]) {
      section.options[index].userValue = userValue;

      // Check if quiz state has changed
      this.hasQuizInputChanged = this.checkQuizStateChanged();
      this.quizInputChanged.emit(this.hasQuizInputChanged);
      this.cdr.detectChanges();
    }
  }

  onOptionChange(event: Event, sectionId: string, index: number) {
    const input = event.target as HTMLInputElement;
    const isChecked = input.checked;

    // Update the section's isChecked state
    const section = this.sections.find((s) => s.id === sectionId);
    if (section && section.options[index]) {
      section.options[index].isChecked = isChecked;

      // Check if quiz state has changed
      this.hasQuizInputChanged = this.checkQuizStateChanged();
      this.quizInputChanged.emit(this.hasQuizInputChanged);
      this.cdr.detectChanges();
    }
  }

  drawArrow(sectionData: ISectionData, index: number) {
    const direction =
      sectionData.propInfo?.find((p) => p.name === 'direction')?.value ||
      'right';
    const strokeColor =
      sectionData.propInfo?.find((p) => p.name === 'strokeColor')?.value ||
      '#000000';
    const strokeWidth =
      sectionData.propInfo?.find((p) => p.name === 'strokeWidth')?.value || '2';
    const fillType =
      sectionData.propInfo?.find((p) => p.name === 'fillType')?.value || '0,0';
    const validWidth =
      Number.isFinite(Number(sectionData.width)) &&
      Number(sectionData.width) > 0
        ? Number(sectionData.width)
        : 100;
    const validHeight =
      Number.isFinite(Number(sectionData.height)) &&
      Number(sectionData.height) > 0
        ? Number(sectionData.height)
        : 50;
    const validStrokeWidth =
      Number.isFinite(Number(strokeWidth)) && Number(strokeWidth) >= 0
        ? Number(strokeWidth)
        : 2;

    const arrow = {
      type: SectionType.Arrow,
      direction,
      strokeColor,
      strokeWidth: validStrokeWidth,
      fillType,
      viewboxValue: `0 0 ${validWidth} ${validHeight}`,
      pathData: this.getArrowPath(
        direction,
        validStrokeWidth,
        validHeight,
        validWidth,
        index,
      ),
    };

    this.sections.push(arrow);
  }

  setPositionAndSize(index: number, sectionData: ISectionData) {
    const x = sectionData.col * this.cellWidth;
    const y = sectionData.row * this.cellHeight;
    const width = sectionData.colCount * this.cellWidthOnLayout;
    const height = sectionData.rowCount * this.cellHeightOnLayout;

    this.sections[index].styles = {
      ...this.sections[index].styles,
      position: 'absolute',
      width: `${width}px`,
      height: `${height}px`,
      top: `${y}px`,
      left: `${x}px`,
      transform: `scale(${this.zoomScale[0]},${this.zoomScale[1]})`,
      transformOrigin: 'top left',
    };
  }

  applyBindingsToElements() {
    if (this.sectionElements) {
      this.sectionElements.toArray().forEach((elementRef, index) => {
        const sectionData: ISectionData = JSON.parse(
          this.pageLayout?.Sections?.[index]?.Data || '{}',
        );
        if (sectionData.type == SectionType.Arrow) {
          const direction =
            sectionData.propInfo?.find((p) => p.name === 'direction')?.value ||
            'right';
          const strokeColor =
            sectionData.propInfo?.find((p) => p.name === 'strokeColor')
              ?.value || '#000000';
          const strokeWidth =
            sectionData.propInfo?.find((p) => p.name === 'strokeWidth')
              ?.value || '2';
          const fillType =
            sectionData.propInfo?.find((p) => p.name === 'fillType')?.value ||
            '0,0';
          const validStrokeWidth =
            Number.isFinite(Number(strokeWidth)) && Number(strokeWidth) >= 0
              ? Number(strokeWidth)
              : 2;
          const arrowSection = this.sections[index];
          const arrowRect = elementRef.nativeElement.getBoundingClientRect();
          const validWidth =
            Number.isFinite(arrowRect.width) && arrowRect.width > 0
              ? arrowRect.width
              : 100;
          const validHeight =
            Number.isFinite(arrowRect.height) && arrowRect.height > 0
              ? arrowRect.height
              : 50;
          arrowSection.viewboxValue = `0 0 ${validWidth} ${validHeight}`;
          arrowSection.pathData = this.getArrowPath(
            direction,
            validStrokeWidth,
            validHeight,
            validWidth,
            index,
          );
        }
        sectionData.propInfo?.forEach((prop) => {
          this.setBindingValue(
            elementRef.nativeElement,
            prop.binding.replace('parentElement.', ''),
            prop.value,
            sectionData.type,
          );
        });
      });
      this.ngZone.run(() => {
        this.cdr.detectChanges();
      });
    }
  }

  public enableQuizSections(): void {
    for (const section of this.sections) {
      if (section.type === SectionType.Option) {
        section.options.forEach((option: any, index: number) => {
          const checkbox = document.querySelector(
            `#opt-${section.id}-${index}`,
          ) as HTMLInputElement;
          if (checkbox) {
            checkbox.disabled = false;
          }
        });
      } else if (section.type === SectionType.TextBox) {
        section.options.forEach((option: any, index: number) => {
          const textbox = document.querySelector(
            `#txt-${section.id}-${index}`,
          ) as HTMLInputElement;
          if (textbox) {
            textbox.readOnly = false;
          }
        });
      }
    }
    this.cdr.detectChanges();
  }

  public disableQuizSections(): void {
    for (const section of this.sections) {
      if (section.type === SectionType.Option) {
        section.options.forEach((option: any, index: number) => {
          const checkbox = document.querySelector(
            `#opt-${section.id}-${index}`,
          ) as HTMLInputElement;
          if (checkbox) {
            checkbox.disabled = true;
          }
        });
      } else if (section.type === SectionType.TextBox) {
        section.options.forEach((option: any, index: number) => {
          const textbox = document.querySelector(
            `#txt-${section.id}-${index}`,
          ) as HTMLInputElement;
          if (textbox) {
            textbox.readOnly = true;
          }
        });
      }
    }
    this.cdr.detectChanges();
  }

  // Check if the current state differs from the initial state
  private checkQuizStateChanged(): boolean {
    for (const section of this.sections) {
      if (section.type === SectionType.Option) {
        const initialOptions = this.initialQuizState[section.id] || [];
        for (let i = 0; i < section.options.length; i++) {
          if (section.options[i].isChecked !== initialOptions[i]?.isChecked) {
            return true;
          }
        }
      } else if (section.type === SectionType.TextBox) {
        const initialOptions = this.initialQuizState[section.id] || [];
        for (let i = 0; i < section.options.length; i++) {
          if (section.options[i].userValue !== initialOptions[i]?.userValue) {
            return true;
          }
        }
      }
    }
    return false;
  }

  // Save the initial state of quiz inputs (checkboxes and textboxes)
  private saveInitialQuizState() {
    this.initialQuizState = {};
    this.sections.forEach((section) => {
      if (section.type === SectionType.Option) {
        this.initialQuizState[section.id] = section.options.map(
          (option: any) => ({
            isChecked: option.isChecked || false,
          }),
        );
      } else if (section.type === SectionType.TextBox) {
        this.initialQuizState[section.id] = section.options.map(
          (option: any) => ({
            userValue: option.userValue || '',
          }),
        );
      }
    });
  }

  // Check Answer
  async checkAnswer(): Promise<boolean> {
    let allCorrect = true;

    for (const section of this.sections) {
      if (section.type === SectionType.Option) {
        section.options.forEach((option: any, index: number) => {
          const checkbox = document.querySelector(
            `#opt-${section.id}-${index}`,
          ) as HTMLInputElement;
          if (checkbox) {
            // Skip optional options (treat them as correct)
            if (!option.isOptional) {
              // Check if any correct non-optional option is unselected
              if (option.isCorrect && !checkbox.checked) {
                allCorrect = false;
              }
              // Mark as incorrect if a non-optional incorrect option is selected
              if (checkbox.checked && !option.isCorrect) {
                allCorrect = false;
              }
            }
          }
        });
      } else if (section.type === SectionType.TextBox) {
        section.options.forEach((option: any, index: number) => {
          const textbox = document.querySelector(
            `#txt-${section.id}-${index}`,
          ) as HTMLInputElement;
          if (textbox) {
            // Only evaluate if the user has entered a value
            if (option.userValue && option.userValue.trim() !== '') {
              const userValues = option.userValue
                .split(',')
                .map((val: string) => val.trim().toLowerCase())
                .filter((val: string) => val !== '');
              const correctValues = option.correctValue
                ? option.correctValue
                    .split(',')
                    .map((val: string) => val.trim().toLowerCase())
                    .filter((val: string) => val !== '')
                : [];
              const optionalValues = option.optionalValue
                ? option.optionalValue
                    .split(',')
                    .map((val: string) => val.trim().toLowerCase())
                    .filter((val: string) => val !== '')
                : [];

              const minimumOptionalValue: number = option.minimumOptionalValue
                ? Number(option.minimumOptionalValue)
                : 0;

              // If correctValues exist, ALL must be present in userValues
              let isCorrect = true;
              if (correctValues.length > 0) {
                // Check if every correct value is included in userValues
                const allCorrectValuesPresent = correctValues.every(
                  (correctVal: any) => userValues.includes(correctVal),
                );
                if (!allCorrectValuesPresent) {
                  isCorrect = false;
                }
              }

              // All user values must be either in correctValues or in optionalValues
              if (isCorrect) {
                // check if minimum optioms added in the answer
                if (userValues.length < minimumOptionalValue) {
                  isCorrect = false;
                } else {
                  for (const userVal of userValues) {
                    if (
                      !correctValues.includes(userVal) &&
                      !optionalValues.includes(userVal)
                    ) {
                      isCorrect = false;
                      break;
                    }
                  }
                }
              }

              if (!isCorrect) {
                allCorrect = false;
              }
            } else {
              // If no input is provided and correctValues exist, mark as incorrect
              if (option.correctValue && option.correctValue.trim() !== '') {
                allCorrect = false;
              } else if (
                option.minimumOptionalValue &&
                Number(option.minimumOptionalValue) > 0
              ) {
                allCorrect = false;
              }
              // If no correctValue exists, empty input is acceptable
            }
          }
        });
      }
    }

    this.cdr.detectChanges();
    return allCorrect;
  }

  resetQuiz() {
    this.sections.forEach((section) => {
      if (section.type === SectionType.Option) {
        section.options.forEach((option: any, index: number) => {
          option.isChecked = false;
          const checkbox = document.querySelector(
            `#opt-${section.id}-${index}`,
          ) as HTMLInputElement;
          if (checkbox) {
            checkbox.checked = false;
          }
        });
      } else if (section.type === SectionType.TextBox) {
        section.options.forEach((option: any, index: number) => {
          option.userValue = '';
          const textbox = document.querySelector(
            `#txt-${section.id}-${index}`,
          ) as HTMLInputElement;
          if (textbox) {
            textbox.value = '';
          }
        });
      }
    });
    // Reset quiz input change tracking
    this.hasQuizInputChanged = false;
    this.saveInitialQuizState(); // Update initila state after reset
    this.quizInputChanged.emit(false);
    this.cdr.detectChanges();
  }

  drawSection(section: ISection, index: number) {
    const sectionData: ISectionData = JSON.parse(section.Data || '{}');
    const drawMethod = this.sectionDrawMethods[sectionData.type];

    if (drawMethod) {
      const initialLength = this.sections.length; // Track initial length before push
      drawMethod(sectionData, index);

      // Ensure that the latest section gets positioned
      if (this.sections.length > initialLength) {
        this.setPositionAndSize(this.sections.length - 1, sectionData);
      }
    } else {
      console.warn(`Unknown section type: ${sectionData.type}`);
    }
  }
}

// Quiz Answer Feedback Dialog
@Component({
  selector: 'quiz-answer-feedback-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="dialog-container">
      <button mat-icon-button class="close-button" (click)="onClose()">
        <mat-icon>close</mat-icon>
      </button>
      <mat-dialog-content>
        <div class="icon-container">
          <div class="icon-circle">
            <mat-icon>check</mat-icon>
          </div>
        </div>
        <p class="message-text" [ngClass]="data.colorClass">
          {{ data.message }}
        </p>
      </mat-dialog-content>
    </div>
  `,
  styles: [
    `
      .dialog-container {
        position: relative;
        min-height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .close-button {
        position: absolute;
        top: 8px;
        right: 8px;
        color: #666;
        border-radius: 0 !important;
        width: 32px;
        height: 32px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .close-button mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        line-height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .close-button::before {
        border-radius: 0 !important;
      }

      .close-button:hover::before {
        border-radius: 0 !important;
        background-color: rgba(0, 0, 0, 0.04) !important;
      }

      .close-button ::ng-deep .mat-mdc-button-persistent-ripple {
        border-radius: 0 !important;
      }

      .close-button ::ng-deep .mat-mdc-button-ripple {
        border-radius: 0 !important;
      }

      .close-button ::ng-deep .mat-mdc-button-touch-target {
        border-radius: 0 !important;
      }

      .message-text {
        margin: 0;
        padding: 0;
        font-size: 30px;
        font-weight: 500;
        text-align: center;
      }

      .orange-text {
        color: #ff9800;
      }

      mat-dialog-content {
        margin: 0;
        padding: 0;
        max-height: none;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
      }

      .icon-container {
        display: flex;
        justify-content: center;
        margin-bottom: 16px;
      }

      .icon-circle {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background-color: #ffcb7f;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .icon-circle mat-icon {
        color: white;
        font-size: 32px;
        width: 32px;
        height: 32px;
      }
    `,
  ],
})
export class QuizAnswerFeedbackDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { message: string; colorClass: string },
    private dialogRef: MatDialogRef<QuizAnswerFeedbackDialogComponent>,
  ) {}

  onClose(): void {
    this.dialogRef.close();
  }
}

// Quiz Textbox Feedback Dialog
@Component({
  selector: 'quiz-textbox-feedback-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="dialog-container">
      <button mat-icon-button class="close-button" (click)="onClose()">
        <mat-icon>close</mat-icon>
      </button>
      <mat-dialog-content>
        <div class="icon-container">
          <div
            class="icon-circle"
            [ngClass]="data.iconType === 'check' ? 'check-icon' : 'cross-icon'"
          >
            <mat-icon>{{
              data.iconType === 'check' ? 'check' : 'close'
            }}</mat-icon>
          </div>
        </div>
        <p class="message-text" [ngClass]="data.colorClass1">
          {{ data.message1 }}
        </p>
        <p
          *ngIf="data.message2"
          class="message-text"
          [ngClass]="data.colorClass2"
        >
          {{ data.message2 }}
        </p>
      </mat-dialog-content>
    </div>
  `,
  styles: [
    `
      .dialog-container {
        position: relative;
        min-height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .close-button {
        position: absolute;
        top: 8px;
        right: 8px;
        color: #666;
        border-radius: 0 !important;
        width: 32px;
        height: 32px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .close-button mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        line-height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .close-button::before {
        border-radius: 0 !important;
      }

      .close-button:hover::before {
        border-radius: 0 !important;
        background-color: rgba(0, 0, 0, 0.04) !important;
      }

      .close-button ::ng-deep .mat-mdc-button-persistent-ripple {
        border-radius: 0 !important;
      }

      .close-button ::ng-deep .mat-mdc-button-ripple {
        border-radius: 0 !important;
      }

      .close-button ::ng-deep .mat-mdc-button-touch-target {
        border-radius: 0 !important;
      }

      .message-text {
        margin: 0;
        padding: 10px;
        font-size: 18px;
        font-weight: 500;
        text-align: center;
      }

      .message-text:last-of-type {
        padding-bottom: 20px;
      }

      .green-text {
        color: #00bd3c;
      }

      .black-text {
        color: #000000;
      }

      .red-text {
        color: #ff0000;
      }

      .fs-1 {
        font-size: 18px !important;
      }

      .fs-2 {
        font-size: 25px !important;
      }

      mat-dialog-content {
        margin: 0;
        padding: 0;
        max-height: none;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
      }

      .icon-container {
        display: flex;
        justify-content: center;
        margin-bottom: 16px;
      }

      .icon-circle {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background-color: #ffcb7f;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .icon-circle mat-icon {
        color: white;
        font-size: 32px;
        width: 32px;
        height: 32px;
      }

      .check-icon {
        background-color: #00bd3c;
      }

      .cross-icon {
        background-color: #ff0000;
      }
    `,
  ],
})
export class QuizTextboxFeedbackDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      message1: string;
      message2: string;
      colorClass1: string;
      colorClass2: string;
      iconType: 'check' | 'cross';
    },
    private dialogRef: MatDialogRef<QuizTextboxFeedbackDialogComponent>,
  ) {}

  onClose(): void {
    this.dialogRef.close();
  }
}
